import { companyId, salesOfficeAreaId } from './constants';
import { Cart } from './context/CartContext';
import {
  GenderType,
  IAccountResult,
  ICalculatedIndividualPoints,
  ICallBackFormInput,
  ICitizenshipResult,
  IGeographicBoundaryResult,
  IGuideSalesIndividualTourAccommodationInfo,
  IIndividualPointResult,
  IIndividualResult,
  IMemberSalesOrderInfoResult,
  IPartyContactInfoInput,
  IPartyContactInfoResult,
  ISalesOrderInput,
  ISalesOrderResult,
  ITourDocumentInfo,
  ITourPaymentInfo,
  ITourVisaInfo,
  SmsType,
} from './utils/types';

export type ApiPaymentBalance = {
  orderNumber: string;
  orderDate: string;
  salesIndividualId: number;
  firstName: string;
  lastName: string;
  pnr: string;
  tourName: string;
  tourStartDate: string;
  bedTypeName: string;
  payment: {
    total: ApiAmountWithCurrency;
    balance: ApiAmountWithCurrency;
  };
};

export type ApiAmountWithCurrency = {
  amount: number;
  currency: ApiCurrency;
  currencyExchangeRate?: number;
};

export type ApiTour = {
  name: string;
  tourCode: string;
  startDate: string;
  endDate: string;
};

export type ApiCurrency = {
  id: number;
  name: string;
  code: string;
};

export type ApiCreditCard = {
  information: {
    bankName?: string | null; // no support in sipay
    cardProgram: string;
    cardScheme: string;
    isCorporate?: false | null; // no support in sipay
    provider: 'Sipay';
    bankLogo?: string;
    cardProgramLogo?: string;
    schemeLogo: string;
    issuerBankId?: number; // no support in sipay
    cardType: 'CREDIT_CARD' | 'DEBIT_CARD';
    currencyId: number;
    currencyCode: string;
    corporate: boolean;
  };
  installments: ApiCreditCardInstalment[];
  installmentWarnings: string[];
};

export type ApiCreditCardInstalment = {
  count: number;
  monthlyAmount: number;
  totalAmount: number;
  vposBankId: number;
};

export type ApiCreditCardForm = {
  ccHolderName?: string;
  ccNo?: string;
  cvv?: string;
  expiry?: string;
};

type ApiPaymentPointDetail = {
  points: number;
  amount: number;
  currency: {
    id: number;
    code: string;
    name: string;
  };
};
export type ApiPaymentPoint = {
  total: ApiPaymentPointDetail;
  available: ApiPaymentPointDetail;
  toBeUsed: ApiPaymentPointDetail;
  currencyExchangeRate?: number;
};

const _fetch = (url: RequestInfo, init?: RequestInit) =>
  fetch(url, {
    ...init,
  }).then((r) => {
    if (!r.ok) {
      try {
        return r.json().then((data) => {
          throw new Error(data.detail || 'Unknown error');
        });
      } catch (e) {
        throw new Error(String(r.status));
      }
    }
    return r.json();
  });

export const currencyExchange = (
  from: string,
  to: string,
  amount: number,
): Promise<ApiAmountWithCurrency> =>
  _fetch(
    `/api/payment/currency-exchange?from=${from}&to=${to}&amount=${amount}&companyId=${companyId}`,
  );

export const balances = (params: {
  lastName: string;
  orderNumber?: string;
  passportNoOrCitizenshipNumber?: string;
}): Promise<ApiPaymentBalance[]> => {
  const { lastName, orderNumber, passportNoOrCitizenshipNumber } = params;
  let query = `/api/payment/balances?lastName=${lastName}&companyId=${companyId}`;

  if (orderNumber) {
    query += `&orderNumber=${orderNumber}`;
  }

  if (passportNoOrCitizenshipNumber) {
    query += `&passportNoOrCitizenshipNumber=${passportNoOrCitizenshipNumber}`;
  }

  return _fetch(query);
};

export const creditCard = ({
  ccNumber,
  amount,
  currencyCode,
}: {
  ccNumber: string;
  amount: number;
  currencyCode: string;
}): Promise<ApiCreditCard> =>
  _fetch(
    `/api/payment/credit-card?ccNumber=${ccNumber}&amount=${amount}&currencyCode=${currencyCode}`,
  );

export const authenticate = async (username: string, password: string) => {
  const response = await fetch('/api/member/authenticate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      username,
      password,
    }),
  });
  return response;
};

export const addMember = async (
  values: {
    firstName?: string;
    lastName?: string;
    birthDate?: string | null;
    gender?: GenderType;
    email?: string;
    phoneNumber?: string;
    password?: string;
    citizenshipNumber?: string | null;
    citizenshipCountryId?: number | null;
    membershipContractApproved: boolean;
  },
  token?: string,
) => {
  const response = await fetch('/api/member', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      ...values,
      companyId: companyId,
    }),
  });

  return response;
};

//send sms for register
export const sendSmsForRegister = async (
  phoneNumber: string,
  type: SmsType,
  notificationType?: string,
) => {
  const response = await fetch('/api/sms-otps', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      phoneNumber,
      type,
      companyId,
      notificationType,
    }),
  });

  return response;
};

export const userRegister = async (phoneNumber: string, otpCode: any) => {
  const response = await fetch('/api/authenticate-phone-number', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      phoneNumber,
      otpCode,
    }),
  });
  return response;
};

export const smsVerification = async (phoneNumber: string, otpCode: any) => {
  const response = await fetch('api/account/reset-password-by-phone-number', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      phoneNumber,
      otpCode,
    }),
  });
  return response;
};

//send sms for register
export const sendSmsForReset = async (
  phoneNumber: string,
  type: SmsType,
  notificationType?: string,
) => {
  const response = await fetch('/api/sms-otps', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      phoneNumber,
      type,
      notificationType,
      companyId,
    }),
  });

  return response;
};

//send sms for register
export const passwordReset = async (key: string, newPassword: string) => {
  const response = await fetch('/api/account/reset-password/finish', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      key,
      newPassword,
    }),
  });

  return response;
};

export const addTransportationRequisition = async (
  authToken?: string,
  arrivalId?: number,
  departureId?: number,
  direction?: string,
  endDate?: string,
  startDate?: string,
  transportationTypeId?: number,
  salesIndividualId?: number,
) => {
  const response = await fetch(
    `/api/member/sales-individuals/${salesIndividualId}/sales-order-item/transportation/bulk`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        arrivalId,
        departureId,
        direction,
        endDate,
        startDate,
        transportationTypeId,
      }),
    },
  );
  return response;
};

export const addGuideTransportationRequisition = async (
  authToken?: string,
  arrivalId?: number,
  departureId?: number,
  direction?: string,
  endDate?: string,
  startDate?: string,
  transportationTypeId?: number,
  salesIndividualId?: number,
  guideSalesIndividualId?: string,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${guideSalesIndividualId}/customers/${salesIndividualId}/sales-order-item/transportation/bulk`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        arrivalId,
        departureId,
        direction,
        endDate,
        startDate,
        transportationTypeId,
      }),
    },
  );
  return response;
};

export const getAirports = async (token: string) => {
  const response = await fetch(
    '/api/geographic-boundaries?typeId.equals=16&size=2000',
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail || 'Hava Limanları Yüklenmedi');
  }

  return response.json();
};

export const account = async (authToken: string) => {
  const response = await fetch(`/api/member/account`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
  });
  if (response.status !== 200) {
    throw new Error('Failed to fetch account');
  }
  return response.json() as Promise<IAccountResult>;
};

export const individualPoints = async (
  authToken?: string,
): Promise<IIndividualPointResult[]> => {
  const response = await fetch(`/api/member/individuals/points`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
  });
  const data = await response.json();

  return data;
};

export const individualCalculatedPoints = async (
  authToken?: string,
): Promise<ICalculatedIndividualPoints> => {
  const response = await fetch(`/api/member/individuals/points-calculation`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
  });
  const data = await response.json();

  return data;
};

export const individualBalances = async (
  guide: boolean,
  authToken?: string,
): Promise<IMemberSalesOrderInfoResult[]> => {
  return await _fetch(
    `/api/member/individuals/sales-order-info?guide=${guide}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
};

export const paymentPoints = (
  params: {
    salesIndividualId: number;
    reservedSalesIndividualIds: number[];
    reservedPoints: number[];
  },
  authToken: string,
): Promise<ApiPaymentPoint> => {
  const queryParams = new URLSearchParams();
  queryParams.set('salesIndividualId', String(params.salesIndividualId));
  queryParams.set(
    'reservedSalesIndividualIds',
    params.reservedSalesIndividualIds.join(','),
  );
  queryParams.set('reservedPoints', params.reservedPoints.join(','));

  return _fetch(`/api/payment/points?${queryParams.toString()}`, {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });
};

export const accommodationInfos = async (
  salesIndividualId: string,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/member/sales-individuals/${salesIndividualId}/accommodation-infos`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const passportInfos = async (
  salesIndividualId: string,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/member/sales-individuals/${salesIndividualId}/passports`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const loyaltyPrograms = async (authToken?: string) => {
  const response = await fetch(`/api/member/individuals/loyalty-programs`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
  });
  return response;
};

export const deleteLoyaltyPrograms = async (id: number, authToken?: string) => {
  const response = await fetch(
    `/api/member/individuals/loyalty-programs/${id}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const organizationSupplierLoyaltyCompanies = async (
  authToken?: string,
) => {
  const response = await fetch(
    `/api/organizations-supplier-loyalty-companies`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const getCountries = async (token: string) => {
  const response = await fetch(
    '/api/geographic-boundaries?typeId.equals=2&size=2000',
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail || 'Ülkeler yüklenemedi');
  }

  return response.json();
};

export const getCities = async (countryId: number, token: string) => {
  const response = await fetch(
    `/api/geographic-boundaries?parentId.equals=${countryId}&size=1000`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail || 'Şehirler yüklenemedi');
  }

  return response.json();
};

export const getDistricts = async (cityId: number, token: string) => {
  const response = await fetch(
    `/api/geographic-boundaries?parentId.equals=${cityId}&size=1000&typeId.in=1,2,3,4,5,6,7,8,9,10,11,12,17&sort=translations.name`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail || 'İlçeler yüklenemedi');
  }

  return response.json();
};

export const getNeighborhoods = async (districtId: number, token: string) => {
  const response = await fetch(
    `/api/geographic-boundaries?parentId.equals=${districtId}&size=1000`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail || 'Mahalleler yüklenemedi');
  }

  return response.json();
};

export const getStreets = async (neighborhoodId: number, token: string) => {
  const response = await fetch(
    `/api/geographic-boundaries?parentId.equals=${neighborhoodId}&size=1000&typeId.in=1,2,3,4,5,6,7,8,9,10,11,12,17&sort=translations.name`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.detail || 'Sokaklar yüklenemedi');
  }

  return response.json();
};

export const addEditExternalContactMedium = async (
  salesIndividualId?: string,
  authToken?: string,
  description?: string,
  electronicAddress?: string,
  electronicAddressInputTypeId?: number,
  fromDate?: string,
  owner?: string,
  countryCode?: string,
  phoneNumber?: string,
  phoneNumberInputTypeId?: number,
  doorNumber?: string,
  geographicBoundaryId?: number,
  postCode?: string,
  streetAddress1?: string,
  postalAddressInputInputTypeId?: number,
  toDate?: string,
) => {
  const url = `/member/sales-individuals/${salesIndividualId}/parties-contact-medium-external-applications`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      description,
      electronicAddressInput: {
        electronicAddress,
        typeId: electronicAddressInputTypeId,
      },
      fromDate,
      owner,
      phoneNumberInput: {
        countryCode,
        phoneNumber,
        typeId: phoneNumberInputTypeId,
      },
      postalAddressInput: {
        doorNumber,
        geographicBoundaryId,
        postCode,
        streetAddress1,
        typeId: postalAddressInputInputTypeId,
      },
      toDate,
    }),
  });

  return response;
};

export const addEditLoyaltyPrograms = async (
  id?: number,
  authToken?: string,
  companyId?: number,
  membershipNumber?: string,
) => {
  const response = await fetch(
    id
      ? `/api/member/individuals/loyalty-programs/${id}`
      : `/api/member/individuals/loyalty-programs`,
    {
      method: id ? 'PUT' : 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        id,
        companyId,
        membershipNumber,
      }),
    },
  );
  return response;
};

export const memberDeactivateAccount = async (authToken?: string) => {
  const response = await fetch(`/api/member/deactivate-account`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
  });
  return response;
};

export const updateCancelSalesIndividualMaterial = async (
  salesIndividualId: number,
  mainSalesOrderItemId: number,
  cancelReasonId: number,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/member/sales-individuals/${salesIndividualId}/sales-order-item/${mainSalesOrderItemId}/update-cancellation`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        cancelReasonId,
      }),
    },
  );

  return response;
};

export const updateBedTypeSalesIndividualMaterial = async (
  salesIndividualId: number,
  pricingId: number | string,
  observe: string,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/member/sales-individuals/${salesIndividualId}/change-rooms/${pricingId}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        observe,
      }),
    },
  );

  return response;
};

export const updateTourTransferSalesIndividualMaterial = async (
  salesIndividualId: number,
  tourId: number | string,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/member/sales-individuals/${salesIndividualId}/tour-transfer/${tourId}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );

  return response;
};

export const getTourPricesOptions = async (
  salesIndividualId: number,
  authToken?: string,
  tourCode?: string,
) => {
  let url = `/api/member/sales-individuals/${salesIndividualId}/prices`;
  if (tourCode) {
    const params = new URLSearchParams({ 'tourCode.equals': tourCode });
    url += `?${params.toString()}`;
  }
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
  });
  return response;
};

export const memberDeactivateAccountConfirmation = async (
  otpCode?: string,
  authToken?: string,
) => {
  const response = await fetch(`/api/member/deactivate-account-confirmation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: otpCode,
  });
  return response;
};

export const earlyPaymentCalculation = async (
  salesIndividualId: number,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/sales-individuals/${salesIndividualId}/early-payment-calculations`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const transportationInfos = async (
  salesIndividualId: number,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/member/sales-individuals/${salesIndividualId}/transportation-requisitions?transportationTypeId=2`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const visaInfos = async (
  salesIndividualId: string,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/member/sales-individuals/${salesIndividualId}/visas`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const guideInfos = async (
  salesIndividualId: string,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/member/sales-individuals/${salesIndividualId}/guide-info`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const guideTourCustomerCount = async (
  salesIndividualId: string,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${salesIndividualId}/tour-customer-count`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const guideSalesIndividualTourGroupInfo = async (
  salesIndividualId: string,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${salesIndividualId}/tour-group-infos`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const guideTourAccommodationInfo = async (
  salesIndividualId: string,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${salesIndividualId}/tour-accommodation-infos`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  if (!response.ok) {
    throw new Error('Failed to fetch tour accommodation info');
  }
  return response.json() as Promise<
    IGuideSalesIndividualTourAccommodationInfo[]
  >;
};

export const accommodationStayPresentation = async (
  stayId?: number,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/accommodation-stays/${stayId}/presentation`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const guideTourTransportationInfo = async (
  salesIndividualId: string,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${salesIndividualId}/tour-transportation-infos?isInRow=true`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const guideTourGeneralCustomerList = async (
  salesIndividualId?: string | number,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${salesIndividualId}/tour-customer-list`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const guideTourMedicalAppointmentList = async (
  salesIndividualId?: string,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${salesIndividualId}/customer-medical-appointments`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const createGuideTourMedicalAppointmentList = async (
  guideSalesIndividualId?: string,
  authToken?: string,
  date?: string,
  time?: string,
  type?: string,
  salesIndividualId?: string | number,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${guideSalesIndividualId}/customers/${salesIndividualId}/medical-appointments`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        date,
        time,
        type,
      }),
    },
  );
  return response;
};

export const deleteMedicalAppointment = async (
  id: number,
  guideSalesIndividualId?: string | number,
  authToken?: string,
  customerId?: number,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${guideSalesIndividualId}/customers/${customerId}/medical-appointments/${id}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const guideTourHealthDisabilityReferenceIds = async (
  authToken?: string,
) => {
  const response = await fetch(
    `/api/reference-codes/find?page=0&size=100&sort=id,asc&referenceTypeId=39`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const guideTourHealthCustomerDisability = async (
  authToken?: string,
  guideSalesIndividualId?: string,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${guideSalesIndividualId}/customer-disabilities`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const guideTourHealthMedicine = async (
  authToken?: string,
  guideSalesIndividualId?: string,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${guideSalesIndividualId}/customer-medicine-report-file`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const guideTourHealthCustomerDialysisFile = async (
  authToken?: string,
  guideSalesIndividualId?: string,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${guideSalesIndividualId}/customer-dialysis-file`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const addEditGuideTourHealthDisability = async (
  id?: number,
  authToken?: string,
  referenceCodeId?: number,
  guideSalesIndividualId?: string | number,
  salesIndividualId?: string | number,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${guideSalesIndividualId}/customers/${salesIndividualId}/disabilities`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        referenceCodeId,
      }),
    },
  );
  return response;
};

export const deleteGuideTourHealthDisability = async (
  id?: number,
  authToken?: string,
  guideSalesIndividualId?: string | number,
  salesIndividualId?: string | number,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${guideSalesIndividualId}/customers/${salesIndividualId}/disabilities/${id}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const deleteGuideTourHealthMedicine = async (
  documentId?: number,
  authToken?: string,
  guideSalesIndividualId?: string | number,
  salesIndividualId?: string | number,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${guideSalesIndividualId}/customers/${salesIndividualId}/health-file/${documentId}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const deleteGuideTourHealthDialysis = async (
  documentId?: number,
  authToken?: string,
  guideSalesIndividualId?: string | number,
  salesIndividualId?: string | number,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${guideSalesIndividualId}/customers/${salesIndividualId}/health-file/${documentId}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const addEditGuideTourHealthMedicineReport = async (
  id?: number,
  authToken?: string,
  guideSalesIndividualId?: string | number,
  salesIndividualId?: string | number,
  file?: File,
) => {
  const formData = new FormData();

  if (file) {
    formData.append('file', file);
  }

  const response = await fetch(
    `/api/guide/sales-individuals/${guideSalesIndividualId}/customers/${salesIndividualId}/medicine-report-file`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      body: formData,
    },
  );

  return response;
};

export const addEditGuideTourHealthDialysisReport = async (
  id?: number,
  authToken?: string,
  guideSalesIndividualId?: string | number,
  salesIndividualId?: string | number,
  file?: File,
) => {
  const formData = new FormData();

  if (file) {
    formData.append('file', file);
  }

  const response = await fetch(
    `/api/guide/sales-individuals/${guideSalesIndividualId}/customers/${salesIndividualId}/dialysis-file`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      body: formData,
    },
  );

  return response;
};

export const guideTourCustomerVisaList = async (
  salesIndividualId: string,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${salesIndividualId}/visa-requisitions`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const guideTourCustomerPaymentStateList = async (
  salesIndividualId: string,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${salesIndividualId}/customer-debts`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const guideTourAccommodationGroupList = async (
  salesIndividualId: string,
  tourPlanId: number,
  authToken: string,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${salesIndividualId}/tour-plans/${tourPlanId}/room-group-requisitions`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const guideTourAccommodationGroupSettledListPrint = async (
  salesIndividualId: string,
  tourPlanId: number,
  authToken: string,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${salesIndividualId}/tour-plans/${tourPlanId}/room-group-requisition-print`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const guideCustomerVisaListPrint = async (
  salesIndividualId: string,
  assignmentIds: number[],
  authToken: string,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${salesIndividualId}/visa-download`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify(assignmentIds),
    },
  );
  return response;
};

export const getGuideSalesIndividualTourDocumentInfo = async (
  salesIndividualId: string,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${salesIndividualId}/tour-document-infos`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  if (!response.ok) {
    throw new Error('Failed to fetch tour document info');
  }
  return response.json() as Promise<ITourDocumentInfo[]>;
};

export const getGuideSalesIndividualTourPaymentInfo = async (
  salesIndividualId: string,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${salesIndividualId}/tour-payment-infos`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  if (!response.ok) {
    throw new Error('Failed to fetch tour payment info');
  }
  return response.json() as Promise<ITourPaymentInfo>;
};

export const getGuideSalesIndividualTourVisaInfo = async (
  salesIndividualId: string,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/guide/sales-individuals/${salesIndividualId}/tour-visa-infos`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  if (!response.ok) {
    throw new Error('Failed to fetch tour visa info');
  }
  return response.json() as Promise<ITourVisaInfo>;
};

export const getSalesIndividualOrderInfo = async (
  salesIndividualId: string,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/member/sales-individuals/${salesIndividualId}/sales-order-info`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const getOrderInfoSalesOrderItem = async (
  salesIndividualId: string,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/member/sales-individuals/${salesIndividualId}/sales-order-items`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const getOrderInfoSalesOrderItemViewInvoice = async (
  salesIndividualId: string,
  salesOrderItemId: number,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/member/sales-individuals/${salesIndividualId}/sales-order-items/${salesOrderItemId}/invoice-print`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const getOrderInfoSalesOrderAccepter = async (
  salesIndividualId: string,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/member/sales-individuals/${salesIndividualId}/sales-order-accepter`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const getOrderInfoPaymentTransaction = async (
  salesIndividualId: string,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/member/sales-individuals/${salesIndividualId}/payment-transactions`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const onlineTourPropertySearch = async (searchType: string) => {
  const response = await fetch(
    `/api/online/tour-property-search?query=${searchType}&salesOfficeAreaId=${salesOfficeAreaId}&size=2000`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
  return response;
};

export const onlineTourPropertySearchFilterType = async (
  searchType: string,
  searchProperty: string,
) => {
  const response = await fetch(
    `/api/online/tour-property-search?query=${searchType}&salesOfficeAreaId=${salesOfficeAreaId}&searchProperty=${searchProperty}&size=2000`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
  return response;
};

export enum SearchOptionType {
  CATEGORY = 'CATEGORY',
  TOUR_DAY_COUNT = 'TOUR_DAY_COUNT',
  TOUR_CONCEPT = 'TOUR_CONCEPT',
  DEPARTURE_CITIES = 'DEPARTURE_CITIES',
  DEPARTURE_AIRPORTS = 'DEPARTURE_AIRPORTS',
  ACC_CITIES = 'ACC_CITIES',
  START_DATE_MONTH = 'START_DATE_MONTH',
  TOUR_PRODUCT_TYPES = 'TOUR_PRODUCT_TYPES',
}

export const onlineTourSearchResultN = async (
  searchOptions: { propertyIds: string; type: SearchOptionType }[],
  startDate: string,
  endDate: string,
  pageNumber: number,
  sizeNumber: number,
  sortType: string,
) => {
  const response = await fetch(
    `/api/online/tour-search?endDate=${endDate}&page=${pageNumber}&salesOfficeAreaId=${salesOfficeAreaId}&size=${sizeNumber}&sort=${sortType}&startDate=${startDate}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: '*/*',
      },
      body: JSON.stringify(searchOptions),
    },
  );
  return response;
};

export const onlineTourPreferredCategories = async (
  isFavoriteOrder: boolean,
) => {
  const response = await fetch(
    `/api/online/preferred-tour-categories?isFavoriteOrder=${isFavoriteOrder}&salesOfficeAreaId=${salesOfficeAreaId}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
  return response;
};

export const onlineTourDetailPage = async (id: number) => {
  const response = await fetch(
    `/api/online/tour-search?id=${id}&salesOfficeAreaId=${salesOfficeAreaId}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id,
        salesOfficeAreaId,
      }),
    },
  );
  return response;
};

export const onlineTourDetailPresentation = async (tourId: number) => {
  const response = await fetch(`/api/online/tours/${tourId}/presentations`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return response;
};

export const onlineTourDetailHotel = async (tourId: number) => {
  const response = await fetch(`/api/online/tours/${tourId}/accommodations`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return response;
};

export const onlineTourDetailTransportation = async (tourId: number) => {
  const response = await fetch(`/api/online/tours/${tourId}/transportations`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  return response;
};

export const guideGeneralCustomerListExcelExport = async (
  salesIndividualId: string,
  authToken: string,
  tourGroupId?: number,
) => {
  const response = await fetch(
    tourGroupId
      ? `/api/guide/sales-individuals/${salesIndividualId}/tour-customer-list/excel-export?tourGroupId=${tourGroupId}`
      : `/api/guide/sales-individuals/${salesIndividualId}/tour-customer-list/excel-export`,

    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return response;
};

export const getGeographicBoundary = async (id: number, authToken: string) => {
  const response = await fetch(`/api/geographic-boundaries/${id}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
  });
  return response.json() as Promise<IGeographicBoundaryResult>;
};
export const getGeographicBoundaryHierarchies = async ({
  id,
  authToken,
}: {
  id: number;
  authToken: string;
}) => {
  const response = await fetch(`/api/geographic-boundaries-hierarchies/${id}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
  });
  return response.json() as Promise<IGeographicBoundaryResult[]>;
};

export class NotAuthenticatedError extends Error {}

export const getFetcher =
  (authToken: string, init?: RequestInit) => async (url: string) => {
    const res = await fetch(url, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: authToken ? `Bearer ${authToken}` : '',
        ...init?.headers,
      },
    });
    if (!res.ok) {
      if (res.status === 401) {
        // If the response is a 401, throw a NotAuthenticatedError
        throw new NotAuthenticatedError('Not authenticated');
      }
      // Attach extra info to the error object.
      const data = await res.json();
      const error = new Error(
        data.detail ||
          data.message ||
          'An error occurred while fetching the data.',
      );
      throw error;
    }
    return res.json();
  };

export const saveIndividualCitizenship = async (
  data: {
    birthCountryId?: number;
    birthDate?: string;
    birthPlace?: string;
    fatherName?: string;
    firstName: string;
    gender: GenderType;
    lastName: string;
    citizenshipNumber: string;
    companyId: number;
    countryId: number;
  },
  authToken: string,
) => {
  const response = await fetch(`/api/online/individual-citizenships`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    let message = 'Failed to save individual citizenship';
    try {
      const d = await response.json();
      message = d.detail?.replaceAll('"', '') || message;
    } catch {
      // noop
    }
    throw new Error(message);
  }
  return response.json() as Promise<{
    individual: IIndividualResult;
    citizenship: ICitizenshipResult;
  }>;
};

export const saveCart = async (authToken: string, data: string) => {
  const response = await fetch(`/api/user-basket/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({ payload: data }),
  });
  if (!response.ok) {
    let message = 'Failed to save cart';
    try {
      const d = await response.json();
      message = d.detail?.replaceAll('"', '') || message;
    } catch {
      // noop
    }
    throw new Error(message);
  }
  return response.json() as Promise<Cart>;
};

export const getCart = async (authToken: string) => {
  const response = await fetch(`/api/user-basket/`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
  });
  if (!response.ok) {
    throw new Error('Failed to get cart');
  }
  return response.json() as Promise<{ id: number; payload: string }>;
};

export const savePartyContactInfo = async (
  authToken: string,
  partyId: number,
  data: IPartyContactInfoInput,
) => {
  const response = await fetch(`/api/parties/${partyId}/contact-info`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const message = 'Failed to save party contact info';
    const d = await response.json();
    throw new Error(d?.detail || message);
  }
  return response.json() as Promise<IPartyContactInfoResult>;
};

export const saveSalesOrderBulk = async (
  authToken: string,
  tourId: number,
  data: ISalesOrderInput[],
) => {
  const response = await fetch(
    `/api/online/tours/${tourId}/sales-area/${salesOfficeAreaId}/sales-orders/bulk`,
    {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );

  if (!response.ok) {
    const message = 'Failed to save sales order bulk';
    const d = await response.json();
    throw new Error(d?.detail || message);
  }

  return response.json() as Promise<ISalesOrderResult[]>;
};

export const getCustomerTourPlanActivity = async (
  salesIndividualId: number,
  authToken?: string,
) => {
  const response = await fetch(
    `/api/member/sales-individuals/${salesIndividualId}/tour-activities`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
    },
  );

  return response;
};

export const saveCallBackForm = async (
  authToken: string,
  data: ICallBackFormInput,
) => {
  const response = await fetch(`/api/member/potential-customers`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const message = 'Failed to save party contact info';
    const d = await response.json();
    throw new Error(d?.detail || message);
  }
  return response.json() as Promise<ICallBackFormInput>;
};
